// Google Maps distance / duration helpers.
// Shares the singleton loader with PlacesAutocomplete so the Maps script is
// loaded on-demand even when no Places field has mounted yet (e.g. opening
// the Review modal straight from the Ready-to-Bill list).
//
// Traffic-aware durations:
//   When callers pass a `departureTime` (a future Date object — Google
//   rejects past timestamps), we add drivingOptions with trafficModel
//   "bestguess" so the response includes `duration_in_traffic`. We use
//   that in preference to the free-flow `duration` whenever present.
//   Distance Matrix Advanced is billed at $10/1k elements vs $5/1k for
//   the basic variant; we soft-track usage in localStorage and emit a
//   console warning at 3,500 elements/month so the owner has early
//   signal before brushing up against the $200/mo free credit.
import { loadMapsScript } from "../components/PlacesAutocomplete";

// ── Soft monthly element budget ──────────────────────────────────────
// Persists a per-month element counter in localStorage. NOT a hard cap
// (we don't want to silently degrade billing accuracy) — just an early-
// warning console.warn once we cross 70% of the free-credit threshold.
//
// Math: $200/mo credit ÷ $10/1k elements = 20,000 elements max under the
// credit. 70% of that = 14,000. But we set the warn threshold lower
// (3,500) because Auto-calc is the highest-frequency caller and we want
// the owner to notice unusual click patterns long before they'd actually
// hit the cap. Adjust BUDGET_WARN_ELEMENTS upward if it becomes noisy.
const BUDGET_STORAGE_KEY = "vf_maps_element_usage";
const BUDGET_WARN_ELEMENTS = 3500;

function thisMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function loadBudget() {
  try {
    const raw = localStorage.getItem(BUDGET_STORAGE_KEY);
    if (!raw) return { month: thisMonthKey(), elements: 0, warned: false };
    const parsed = JSON.parse(raw);
    if (parsed.month !== thisMonthKey()) {
      return { month: thisMonthKey(), elements: 0, warned: false };
    }
    return parsed;
  } catch {
    return { month: thisMonthKey(), elements: 0, warned: false };
  }
}

function saveBudget(state) {
  try {
    localStorage.setItem(BUDGET_STORAGE_KEY, JSON.stringify(state));
  } catch { /* swallow — quota errors etc. shouldn't break Maps calls */ }
}

/**
 * Increment the monthly element counter. Called once per Distance Matrix
 * element returned. Emits a one-time console.warn the moment we cross
 * BUDGET_WARN_ELEMENTS for the current calendar month. Resets on month
 * rollover (driven off thisMonthKey()).
 */
function trackElements(n, traffic) {
  if (!n || n <= 0) return;
  const state = loadBudget();
  state.elements += n;
  if (!state.warned && state.elements >= BUDGET_WARN_ELEMENTS) {
    state.warned = true;
    // Two-line message so it's obvious in the console and copy-pastes well.
    // eslint-disable-next-line no-console
    console.warn(
      `[VeloceFlow] Google Maps Distance Matrix is at ~${state.elements} elements ` +
      `for ${state.month} (~${traffic ? "$" + (state.elements / 100).toFixed(2) + " w/ traffic" : "$" + (state.elements / 200).toFixed(2)}). ` +
      `$200/mo credit kicks in at ~20k elements w/ traffic. Heads up.`
    );
  }
  saveBudget(state);
}

/**
 * Read the current month's usage. Exposed so the owner UI can render a
 * "this month: X elements / ~$Y" chip somewhere if we want.
 */
export function getMapsBudgetUsage() {
  const state = loadBudget();
  // Estimated cost — assume "with traffic" pricing since that's the
  // default for all auto-calc clicks now. $10 per 1k elements.
  const estDollars = (state.elements / 1000) * 10;
  return {
    month: state.month,
    elements: state.elements,
    estDollars,
    warnThreshold: BUDGET_WARN_ELEMENTS,
  };
}

async function waitForMaps(timeoutMs = 15000) {
  if (window.google?.maps?.DistanceMatrixService) return window.google.maps;
  // Kick off the loader (idempotent singleton inside PlacesAutocomplete).
  loadMapsScript().catch(() => { /* handled below by timeout */ });
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const i = setInterval(() => {
      if (window.google?.maps?.DistanceMatrixService) {
        clearInterval(i);
        resolve(window.google.maps);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(i);
        reject(new Error("Google Maps not loaded"));
      }
    }, 100);
  });
}

function toLatLng(p) {
  if (p == null) return null;
  if (typeof p.lat === "number" && typeof p.lng === "number") return { lat: p.lat, lng: p.lng };
  return null;
}

/**
 * Geocode an address or free-form string → { lat, lng } or null.
 * Used as a last resort when a stop has no stored coordinates.
 */
export async function geocode(query) {
  if (!query || !String(query).trim()) return null;
  const maps = await waitForMaps();
  const geocoder = new maps.Geocoder();
  return new Promise((resolve) => {
    geocoder.geocode({ address: String(query) }, (results, status) => {
      if (status !== "OK" || !results?.[0]) return resolve(null);
      const loc = results[0].geometry?.location;
      if (!loc) return resolve(null);
      resolve({ lat: loc.lat(), lng: loc.lng() });
    });
  });
}

/**
 * Coerce a caller-supplied departureTime into a Date that Google will
 * accept. Distance Matrix Advanced ONLY accepts now-or-future Dates —
 * a past timestamp throws "departure_time is in the past". For a job
 * that already happened we still want a sensible traffic estimate, so
 * we project to the SAME time-of-day on the next occurrence of that
 * weekday (typical traffic for "Tuesday 8 AM" is a fine proxy).
 *
 * Returns:
 *   - A future Date (the caller's input or its weekday projection), or
 *   - null when the input is unparseable (caller should omit
 *     drivingOptions in that case so the request stays Basic).
 */
function normalizeDepartureTime(input) {
  if (!input) return null;
  const d = input instanceof Date ? new Date(input.getTime()) : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const now = Date.now();
  if (d.getTime() >= now) return d;
  // Past — bump forward by whole weeks until it's at-or-after "now".
  // (One week = 7 × 86_400_000 ms = 604_800_000.) Same weekday + same
  // hour/minute as the original, which is what we want for traffic
  // pattern preservation.
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const projected = new Date(d.getTime());
  while (projected.getTime() < now) projected.setTime(projected.getTime() + WEEK_MS);
  return projected;
}

/**
 * Compute driving distance (miles) and duration (seconds) between two points.
 * Returns { miles, seconds } or null if either point is missing lat/lng.
 *
 * options.departureTime — optional Date. When set, the request becomes
 *   traffic-aware (trafficModel: bestguess) and `seconds` reflects the
 *   estimated duration in traffic at that departure time. Past dates
 *   are auto-projected forward to the same weekday/time-of-day so a
 *   "rebill this job from last Tuesday" still gets a realistic estimate
 *   instead of free-flow speed.
 */
export async function driveBetween(from, to, options = {}) {
  const a = toLatLng(from);
  const b = toLatLng(to);
  if (!a || !b) return null;
  const maps = await waitForMaps();
  const svc = new maps.DistanceMatrixService();
  const departureTime = normalizeDepartureTime(options.departureTime);

  const request = {
    origins: [a],
    destinations: [b],
    travelMode: maps.TravelMode.DRIVING,
    unitSystem: maps.UnitSystem.IMPERIAL,
  };
  if (departureTime) {
    request.drivingOptions = {
      departureTime,
      trafficModel: maps.TrafficModel?.BEST_GUESS || "bestguess",
    };
  }

  return new Promise((resolve) => {
    svc.getDistanceMatrix(request, (res, status) => {
      if (status !== "OK" || !res?.rows?.[0]?.elements?.[0]) {
        // Still count the attempted element so usage estimates don't
        // drift below actual billing — Google bills failed elements
        // too in some cases. Cheap insurance against under-warning.
        trackElements(1, !!departureTime);
        // A silent null here reads to the owner as "no coords" on the
        // billing panel, which is almost never the real cause. Name the
        // Google status so a REQUEST_DENIED / OVER_QUERY_LIMIT / key or
        // billing problem is diagnosable from the console.
        // eslint-disable-next-line no-console
        console.warn("[VeloceFlow] Distance Matrix request failed:", status, { from: a, to: b, traffic: !!departureTime });
        return resolve(null);
      }
      const el = res.rows[0].elements[0];
      if (el.status !== "OK") {
        trackElements(1, !!departureTime);
        // eslint-disable-next-line no-console
        console.warn("[VeloceFlow] Distance Matrix leg not routable:", el.status, { from: a, to: b });
        return resolve(null);
      }
      trackElements(1, !!departureTime);
      // Orion FAQ: "Please enter whole numbers for mileage — if the mileage
      // is fractional, please round up to the next whole number (12.7 = 13)."
      // Also enforce a 1-mile minimum per leg — same-plaza stops (e.g. two
      // stores on 4400 Sharon Rd) often geocode to identical coords and
      // Google returns 0 meters, but the guard still drove between them.
      //
      // Prefer duration_in_traffic when present (only set on Advanced
      // requests with a departureTime); otherwise fall back to the
      // free-flow duration so Basic callers keep working unchanged.
      const seconds = el.duration_in_traffic?.value ?? el.duration?.value ?? 0;
      resolve({
        miles: Math.max(1, Math.ceil(el.distance.value / 1609.344)),
        seconds,
      });
    });
  });
}

/**
 * Compute consecutive-pair driving distances for an ordered list of points.
 * Returns an array of { miles, seconds } — length = points.length - 1.
 * Any pair missing lat/lng returns null in that slot.
 *
 * options.departureTime — passed straight through to every driveBetween
 *   call. Using a single "leave home" timestamp across the whole route
 *   is a reasonable approximation; the cumulative drift over a multi-
 *   stop route is small relative to traffic-model uncertainty.
 */
export async function driveAlongRoute(points, options = {}) {
  const results = [];
  for (let i = 0; i < points.length - 1; i++) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await driveBetween(points[i], points[i + 1], options));
  }
  return results;
}
